import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  isUserConsentCurrent,
  recordUserConsent,
  watchUserConsent,
} from '../services/consentService'
import type { UserConsentRecord } from '../types/consent'

interface ConsentState {
  loading: boolean
  userConsent: UserConsentRecord | null
  needsUserConsent: boolean
  acceptUserConsent: () => Promise<void>
}

/**
 * Admin-side consent hook. The admin surface only enforces user-level
 * ToS + Privacy acceptance — the org DPA flow is manager-only.
 *
 * Subscribes to the consent record rather than reading it once, so:
 *  - `loading` stays true (popup hidden) until Firestore gives a definitive
 *    answer — a transient read failure can no longer masquerade as "never
 *    accepted" and re-prompt a consented user;
 *  - accepting in one tab/device closes the popup in every other open tab.
 */
export function useConsent(): ConsentState {
  const { currentUser } = useAuth()
  const [loading, setLoading] = useState(true)
  const [userConsent, setUserConsent] = useState<UserConsentRecord | null>(null)

  useEffect(() => {
    if (!currentUser) {
      setUserConsent(null)
      setLoading(false)
      return undefined
    }
    setLoading(true)
    return watchUserConsent(currentUser.uid, (record) => {
      setUserConsent(record)
      setLoading(false)
    })
  }, [currentUser])

  const acceptUserConsent = useCallback(async () => {
    if (!currentUser) return
    // No manual refetch: the snapshot listener fires on the local write and
    // clears `needsUserConsent` immediately.
    await recordUserConsent(currentUser.uid, 'admin')
  }, [currentUser])

  return {
    loading,
    userConsent,
    needsUserConsent: !loading && !!currentUser && !isUserConsentCurrent(userConsent),
    acceptUserConsent,
  }
}
